import { onCleanup } from 'solid-js'
import { useServer } from 'Shared/SiteProvider'
import { browserId } from 'Browser/BrowserId'
import { useImportExport } from './importExport'
import type { HistoryContext } from './history'
import { Mode } from './PosterMode'
import type { ObjectContext } from './objects'
import { bakeLinkedPositions } from './objects'
import type { DesignLinkContext } from './designLink'
import type { SelectionContext } from '../canvas/selection'
import type { CanvasContext } from '../canvas/canvas'
import { ISave } from 'Shared/posters/project/PosterRenderer'

const DEBOUNCE_MS = 1500

export interface IAutoSave {
	posterId:          string
	mode: Mode;
	objects: ObjectContext;
	selection: SelectionContext;
	canvas: CanvasContext;
	history: HistoryContext;
	design: DesignLinkContext;
	save: ISave;
}

/**
 * Autosaves the poster design to the DB whenever the history changes.
 * Must be rendered inside PosterProvider (needs ModeProvider, importExport context).
 * Must also be inside SiteProvider (needs useServer).
 */
export function AutoSave(P: IAutoSave): null {
	const server    = useServer()
	const histCtx   = P.history
	const { exportPoster } = useImportExport(P.objects, P.selection, P.canvas)

//TODO convert this over from being a component

//TODO remove these defaults

	const page       = P.save.page       ?? 'admin/Poster'
	const permission = P.save.permission ?? 'saveDesign'
	const location   = P.save.location   ?? ['design']
	const guardMode  = P.save.onlyInDesignMode !== false

	let timer: ReturnType<typeof setTimeout> | undefined

	// History changes are all event-driven (command execute / undo / redo / markDirty), so we
	// subscribe to them directly rather than reacting to a signal with createEffect.
	function scheduleSave(): void {
		if (guardMode && P.mode != 'design') return  // Ephemeral user-mode changes

		arm()
	}

	function arm(): void {
		clearTimeout(timer)
		timer = setTimeout(fire, DEBOUNCE_MS)
	}

	async function fire(): Promise<void> {
		if (guardMode && P.mode != 'design') {
			arm()
			return
		}
		try {
			// User mode renders the linked layout live; bake it into the store here so the saved design
			// carries the record-responsive positions rather than the stale flatten it started from.
			if (P.mode == 'user' && P.design.linked())
				bakeLinkedPositions(P.objects, P.selection.impls, P.design)

			const design = exportPoster()
			await (server as any).replace.mutate({
				page,
				permission,
				id:       browserId(P.posterId),
				location,
				fields:   design,
			})
		} catch (err) {
			console.error('[AutoSave] Failed to save poster design:', err)
		}
	}

	const unsubscribe = histCtx.onChange(scheduleSave)

	onCleanup(() => {
		clearTimeout(timer);
		unsubscribe();
	})

	return null
}
